import React, { FC, useCallback, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";

import { apiClient } from "../../../../api/client";
import { url } from "../../../../api";
import { useConfirm } from "../../../../ui/components/ConfirmDialog";

import type {
  CalendarEntry,
  CalendarResponse,
  CalendarStats,
  ContestedLossEntry,
  FastestAOTDEntry,
  FavoriteSelection,
  FavoritesResponse,
  RaceDay,
  RaceEntryTimeEntry,
  RaceStatsResponse,
} from "./types";
import {
  AvgEntryBars,
  DailyRaceTimeline,
  WinRateBars,
} from "./RaceStatsCharts";
import {
  Btn,
  Card,
  Disclosure,
  ErrorText,
  Hint,
  Input,
  Pill,
  Row,
  Select,
  Table,
  TableScroll,
  colors,
} from "./styles";

const CALENDAR_API = `${url}/api/aotd-calendar`;
const FAVORITES_API = `${url}/api/art-of-the-day/2025-favorites`;
const FASTEST_API = `${url}/api/site-stats/fastest-aotd`;
const RACE_STATS_API = `${url}/api/site-stats/aotd-race-stats`;

const PageRow = styled(Row)`
  margin-top: 12px;
  span {
    color: ${colors.textMuted};
    font-size: 0.85rem;
  }
`;

function formatOffsetMs(ms: number): string {
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) return `+${totalSeconds.toFixed(2)}s`;
  const rounded = Math.round(totalSeconds);
  const h = Math.floor(rounded / 3600);
  const m = Math.floor((rounded % 3600) / 60);
  const s = rounded % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0 || h > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return `+${parts.join(" ")}`;
}

const FavoritesTool: FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selections, setSelections] = useState<FavoriteSelection[]>([]);
  const [deadline, setDeadline] = useState<string | null>(null);
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<FavoritesResponse>(
        `${FAVORITES_API}/admin/all`,
        {},
      );
      if (res.data.success && res.data.data) {
        setSelections(res.data.data.selections);
        setDeadline(res.data.data.deadline);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fetch favorites.");
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Disclosure onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && !selections.length && !error && fetchAll()}>
      <summary>
        <span style={{ flex: 1 }}>⭐ 2025 Favorites</span>
        <Pill $tone="muted">{selections.length} selections</Pill>
      </summary>
      <div>
        <Row>
          <Btn onClick={fetchAll} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Btn>
          {deadline && (
            <span style={{ color: colors.textMuted, fontSize: "0.85rem" }}>
              Deadline: {new Date(deadline).toLocaleDateString()}
            </span>
          )}
        </Row>
        {error && <ErrorText>{error}</ErrorText>}
        {selections.length > 0 ? (
          <TableScroll style={{ marginTop: 10 }}>
            <Table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>AOTD</th>
                  <th>AOTD Date</th>
                  <th>Emojis</th>
                  <th>Selected at</th>
                </tr>
              </thead>
              <tbody>
                {selections.map((s) => (
                  <tr key={s.username}>
                    <td>
                      <strong>{s.username}</strong>
                    </td>
                    <td>{s.aotd?.title || "—"}</td>
                    <td>
                      {s.aotd?.date
                        ? new Date(s.aotd.date).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>{s.aotd?.emojis?.join(" ") || "—"}</td>
                    <td>{new Date(s.selectedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        ) : (
          !loading && !error && <Hint>No selections yet.</Hint>
        )}
      </div>
    </Disclosure>
  );
};

const CalendarTool: FC = () => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [stats, setStats] = useState<CalendarStats[]>([]);
  const [page, setPage] = useState(0);
  const [days, setDays] = useState(90);
  const PER_PAGE = 14;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<CalendarResponse>(`${CALENDAR_API}/full`, {});
      if (res.data.success && res.data.data) {
        setEntries(res.data.data.entries);
        setStats(res.data.data.stats || []);
        setEnabled(true);
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setEnabled(false);
        setError("Calendar feature is not enabled on the server.");
      } else {
        setError(err.response?.data?.message || "Failed to fetch calendar.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const regenerate = useCallback(async () => {
    const ok = await confirm({
      title: "Regenerate calendar?",
      message: `This will regenerate the calendar for ${days} days.`,
      confirmLabel: "Rebuild",
      tone: "danger",
    });
    if (!ok) return;
    setLoading(true);
    try {
      const res = await apiClient.post(
        `${CALENDAR_API}/generate`,
        { days },
        {},
      );
      if (res.data.success) {
        toast.success(
          `Calendar generated with ${res.data.data?.count || 0} entries.`,
        );
        await fetchAll();
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || "Failed to generate calendar.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [days, confirm, fetchAll]);

  const updateEntry = async (date: string, utcOffset: number) => {
    setLoading(true);
    try {
      await apiClient.put(`${CALENDAR_API}/${date}`, { utcOffset }, {});
      toast.success(`Updated ${date} → UTC+${utcOffset}`);
      setEntries((prev) =>
        prev.map((e) =>
          e.date === date
            ? { ...e, utcOffset, timezone: utcOffset === 0 ? "UTC" : `UTC+${utcOffset}` }
            : e,
        ),
      );
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update entry.");
    } finally {
      setLoading(false);
    }
  };

  const paginated = entries.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalPages = Math.ceil(entries.length / PER_PAGE);

  return (
    <Disclosure onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && !entries.length && !error && fetchAll()}>
      <summary>
        <span style={{ flex: 1 }}>📅 Calendar (beta)</span>
        <Pill $tone="muted">{entries.length} entries</Pill>
      </summary>
      <div>
        {!enabled && <ErrorText>{error}</ErrorText>}
        {enabled && (
          <>
            <Row>
              <span style={{ color: colors.textMuted, fontSize: "0.85rem" }}>
                Days to generate:
              </span>
              <Input
                type="number"
                min={1}
                max={730}
                value={days}
                onChange={(e) =>
                  setDays(
                    Math.max(1, Math.min(730, parseInt(e.target.value) || 90)),
                  )
                }
                style={{ width: 80, maxWidth: "100%" }}
                disabled={loading}
              />
              <Btn $variant="primary" onClick={regenerate} disabled={loading}>
                {loading ? "…" : `Regenerate ${days} days`}
              </Btn>
              <Btn onClick={fetchAll} disabled={loading}>
                Refresh
              </Btn>
            </Row>
            {error && enabled && <ErrorText>{error}</ErrorText>}
            {stats.length > 0 && (
              <Row style={{ marginTop: 10 }}>
                {stats.map((s) => (
                  <Pill key={s.offset} $tone="muted">
                    {s.label}: {s.count}d
                  </Pill>
                ))}
              </Row>
            )}
            {entries.length > 0 && (
              <>
                <TableScroll style={{ marginTop: 12 }}>
                  <Table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Scheduled (UTC)</th>
                        <th>TZ</th>
                        <th>Local</th>
                        <th>Status</th>
                        <th>Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((e) => (
                        <tr key={e.date}>
                          <td>{e.date}</td>
                          <td>{new Date(e.scheduledTime).toUTCString()}</td>
                          <td>{e.timezone}</td>
                          <td>{e.localTimeDisplay}</td>
                          <td>{e.status}</td>
                          <td>
                            <Select
                              value={e.utcOffset}
                              onChange={(ev) =>
                                updateEntry(e.date, parseInt(ev.target.value))
                              }
                              disabled={loading}
                            >
                              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                                (o) => (
                                  <option key={o} value={o}>
                                    {o === 0 ? "UTC" : `UTC+${o}`}
                                  </option>
                                ),
                              )}
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableScroll>
                <PageRow>
                  <Btn
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    ← Prev
                  </Btn>
                  <span>
                    Page {page + 1} of {totalPages}
                  </span>
                  <Btn
                    onClick={() =>
                      setPage((p) => Math.min(totalPages - 1, p + 1))
                    }
                    disabled={page >= totalPages - 1}
                  >
                    Next →
                  </Btn>
                </PageRow>
              </>
            )}
            {entries.length === 0 && !loading && !error && (
              <Hint>No calendar entries — click "Regenerate" to populate.</Hint>
            )}
          </>
        )}
      </div>
    </Disclosure>
  );
};

const FastestTool: FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<FastestAOTDEntry[]>([]);
  const [page, setPage] = useState(0);
  const PER_PAGE = 20;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<{ rankings: FastestAOTDEntry[] }>(
        FASTEST_API,
        {},
      );
      setEntries(res.data.rankings || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch leaderboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Disclosure onToggle={(e) => (e.currentTarget as HTMLDetailsElement).open && !entries.length && !error && fetchAll()}>
      <summary>
        <span style={{ flex: 1 }}>⚡ Fastest leaderboard</span>
        <Pill $tone="muted">{entries.length} ranked</Pill>
      </summary>
      <div>
        <Row>
          <Btn onClick={fetchAll} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Btn>
        </Row>
        {error && <ErrorText>{error}</ErrorText>}
        {entries.length > 0 && (
          <>
            <TableScroll style={{ marginTop: 10 }}>
              <Table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Author</th>
                    <th>Offset</th>
                    <th>Date</th>
                    <th>Title</th>
                  </tr>
                </thead>
                <tbody>
                  {entries
                    .slice(page * PER_PAGE, (page + 1) * PER_PAGE)
                    .map((e) => (
                      <tr key={e._id}>
                        <td>
                          <strong>{e.rank}</strong>
                        </td>
                        <td>
                          {e.userEmoji ? `${e.userEmoji} ` : ""}
                          {e.createdBy}
                        </td>
                        <td
                          style={{
                            color:
                              e.rank <= 3
                                ? colors.purple
                                : e.rank <= 10
                                ? colors.ok
                                : undefined,
                            fontWeight: e.rank <= 3 ? 700 : undefined,
                          }}
                        >
                          {formatOffsetMs(e.offsetMs)}
                        </td>
                        <td>{new Date(e.date).toLocaleDateString()}</td>
                        <td>{e.title || "—"}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </TableScroll>
            {entries.length > PER_PAGE && (
              <PageRow>
                <Btn
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Prev
                </Btn>
                <span>
                  Page {page + 1} of {Math.ceil(entries.length / PER_PAGE)}
                </span>
                <Btn
                  onClick={() =>
                    setPage((p) =>
                      Math.min(
                        Math.ceil(entries.length / PER_PAGE) - 1,
                        p + 1,
                      ),
                    )
                  }
                  disabled={page >= Math.ceil(entries.length / PER_PAGE) - 1}
                >
                  Next →
                </Btn>
              </PageRow>
            )}
          </>
        )}
        {entries.length === 0 && !loading && !error && (
          <Hint>No leaderboard data.</Hint>
        )}
      </div>
    </Disclosure>
  );
};

function winRatePct(rate: number): string {
  return `${(rate * 100).toFixed(rate === 0 || rate === 1 ? 0 : 1)}%`;
}

function winRateTone(rate: number): "ok" | "warn" | "err" {
  if (rate >= 0.5) return "ok";
  if (rate >= 0.25) return "warn";
  return "err";
}

const RaceStatsTool: FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contested, setContested] = useState<ContestedLossEntry[]>([]);
  const [entryTimes, setEntryTimes] = useState<RaceEntryTimeEntry[]>([]);
  const [perDay, setPerDay] = useState<RaceDay[]>([]);
  const [windowMs, setWindowMs] = useState(10000);
  const [launchDate, setLaunchDate] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<RaceStatsResponse>(RACE_STATS_API, {});
      setContested(res.data.contested || []);
      setEntryTimes(res.data.entryTimes || []);
      setPerDay(res.data.perDay || []);
      setWindowMs(res.data.entryWindowMs || 10000);
      setLaunchDate(res.data.raceLaunchDate || null);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch race stats.");
    } finally {
      setLoading(false);
    }
  }, []);

  // the "biggest loser" — lowest win rate (lost most OF THE TIME). The list is
  // already sorted by win rate asc; require a few contests so a 1-game fluke
  // doesn't steal the crown, falling back to the worst rate if nobody qualifies.
  const topLoser =
    contested.find((c) => c.contests >= 3 && c.losses > 0) ||
    contested.find((c) => c.losses > 0) ||
    null;

  return (
    <Disclosure
      onToggle={(e) =>
        (e.currentTarget as HTMLDetailsElement).open &&
        !contested.length &&
        !entryTimes.length &&
        !error &&
        fetchAll()
      }
    >
      <summary>
        <span style={{ flex: 1 }}>🏁 Race stats</span>
        <Pill $tone="muted">{contested.length} contested</Pill>
      </summary>
      <div>
        <Row>
          <Btn onClick={fetchAll} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Btn>
          {launchDate && (
            <span style={{ color: colors.textMuted, fontSize: "0.85rem" }}>
              Timed race since {launchDate} (earlier "click-to-claim" days
              reconstructed)
            </span>
          )}
        </Row>
        {error && <ErrorText>{error}</ErrorText>}

        {topLoser && (
          <Hint style={{ marginTop: 10 }}>
            👑 Biggest loser:{" "}
            <strong style={{ color: colors.text }}>
              {topLoser.userEmoji ? `${topLoser.userEmoji} ` : ""}
              {topLoser.username}
            </strong>{" "}
            — {winRatePct(topLoser.winRate)} win rate ({topLoser.wins}W /{" "}
            {topLoser.losses}L across {topLoser.contests} contested races).
          </Hint>
        )}

        {/* ── Charts ── */}
        {perDay.length > 0 && (
          <>
            <PageRow style={{ marginTop: 12 }}>
              <span>
                Daily races —{" "}
                <span style={{ color: colors.ok }}>green = contested</span>,{" "}
                <span style={{ color: colors.textDim }}>grey = solo</span> (bar
                height = racers)
              </span>
            </PageRow>
            <DailyRaceTimeline perDay={perDay} />
          </>
        )}
        {contested.length > 0 && (
          <>
            <PageRow style={{ marginTop: 14 }}>
              <span>Win rate among contested races (biggest loser first)</span>
            </PageRow>
            <WinRateBars contested={contested} />
          </>
        )}
        {entryTimes.length > 0 && (
          <>
            <PageRow style={{ marginTop: 14 }}>
              <span>
                Average entry time — first {Math.round(windowMs / 1000)}s after
                unlock (solo wins under {Math.round(windowMs / 1000)}s included)
              </span>
            </PageRow>
            <AvgEntryBars entryTimes={entryTimes} />
          </>
        )}

        {/* ── Most contested losses + win rate ── */}
        {contested.length > 0 && (
          <>
            <PageRow style={{ marginTop: 6 }}>
              <span>Lost most of the time (sorted by win rate, lowest first)</span>
            </PageRow>
            <TableScroll style={{ marginTop: 6 }}>
              <Table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Losses</th>
                    <th>Wins</th>
                    <th>Contests</th>
                    <th>Win rate</th>
                  </tr>
                </thead>
                <tbody>
                  {contested.map((c, i) => (
                    <tr key={c.userId}>
                      <td>
                        <strong>{i + 1}</strong>
                      </td>
                      <td>
                        {c.userEmoji ? `${c.userEmoji} ` : ""}
                        {c.username}
                      </td>
                      <td style={{ color: colors.err, fontWeight: 700 }}>
                        {c.losses}
                      </td>
                      <td>{c.wins}</td>
                      <td>{c.contests}</td>
                      <td>
                        <Pill $tone={winRateTone(c.winRate)}>
                          {winRatePct(c.winRate)}
                        </Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          </>
        )}

        {/* ── Average race entry time ── */}
        {entryTimes.length > 0 && (
          <>
            <PageRow style={{ marginTop: 16 }}>
              <span>
                Average race entry time (first {Math.round(windowMs / 1000)}s
                after unlock only)
              </span>
            </PageRow>
            <TableScroll style={{ marginTop: 6 }}>
              <Table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Avg entry</th>
                    <th>Fastest</th>
                    <th>Races</th>
                  </tr>
                </thead>
                <tbody>
                  {entryTimes.map((e, i) => (
                    <tr key={e.userId}>
                      <td>
                        <strong>{i + 1}</strong>
                      </td>
                      <td>
                        {e.userEmoji ? `${e.userEmoji} ` : ""}
                        {e.username}
                      </td>
                      <td
                        style={{
                          color:
                            i === 0
                              ? colors.purple
                              : i < 3
                              ? colors.ok
                              : undefined,
                          fontWeight: i < 3 ? 700 : undefined,
                        }}
                      >
                        {formatOffsetMs(e.avgOffsetMs)}
                      </td>
                      <td style={{ color: colors.textMuted }}>
                        {formatOffsetMs(e.fastestOffsetMs)}
                      </td>
                      <td>{e.samples}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          </>
        )}

        {!loading &&
          !error &&
          contested.length === 0 &&
          entryTimes.length === 0 && <Hint>No race data yet.</Hint>}
      </div>
    </Disclosure>
  );
};

export const AdminTools: FC = () => {
  return (
    <Card style={{ padding: 14 }}>
      <FavoritesTool />
      <CalendarTool />
      <FastestTool />
      <RaceStatsTool />
    </Card>
  );
};
